import React from "react";
import { motion } from "framer-motion";
import {
  RiFireLine,
  RiTimeLine,
  RiBarChart2Line,
  RiFlashlightLine,
  RiCalendarCheckLine,
  RiFocus3Line,
} from "react-icons/ri";
import WeeklyActivityChart from "./WeeklyActivityChart";
import { SkeletonBlock } from "../ui/Skeleton/Skeleton";
import ErrorState from "../ui/ErrorState/ErrorState";
import { useWeeklySnapshot } from "../../hooks/useWeeklySnapshot";
import { formatDuration, formatNumber } from "../../utils/helpers";
import "./WeeklySnapshot.css";

const STAT_DEFS = [
  { key: "totalWorkouts", icon: RiFlashlightLine, label: "Workouts", format: (v) => v },
  { key: "totalDurationMin", icon: RiTimeLine, label: "Duration", format: formatDuration },
  { key: "totalVolumeKg", icon: RiBarChart2Line, label: "Volume", format: (v) => `${formatNumber(v)} kg` },
  { key: "totalCalories", icon: RiFireLine, label: "Calories", format: formatNumber },
  { key: "activeDays", icon: RiCalendarCheckLine, label: "Active Days", format: (v) => v },
  { key: "streak", icon: RiFireLine, label: "Streak", format: (v) => `${v}d` },
];

const WeeklySnapshot = () => {
  const { data, isLoading, isError, refetch } = useWeeklySnapshot();

  if (isError) {
    return <ErrorState title="Couldn't load your weekly snapshot" onRetry={refetch} />;
  }

  if (isLoading || !data) {
    return (
      <div className="weekly-snapshot weekly-snapshot--loading">
        <SkeletonBlock width={110} height={14} />
        <div className="weekly-snapshot-stats">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonBlock key={i} height={64} radius={14} />
          ))}
        </div>
        <SkeletonBlock height={120} radius={14} />
      </div>
    );
  }

  const goalPct = Math.round((data.weeklyGoal.current / data.weeklyGoal.target) * 100);

  return (
    <motion.section
      className="weekly-snapshot"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      aria-label="This week's training summary"
    >
      <span className="weekly-snapshot-eyebrow">This Week</span>

      <div className="weekly-snapshot-stats">
        {STAT_DEFS.map(({ key, icon: Icon, label, format }) => (
          <div className="weekly-stat" key={key}>
            <Icon className="weekly-stat-icon" size={18} />
            <div>
              <p className="weekly-stat-value">{format(data[key])}</p>
              <p className="weekly-stat-label">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="weekly-goal">
        <div className="weekly-goal-heading">
          <RiFocus3Line size={16} />
          <span>
            Goal — {data.weeklyGoal.current}/{data.weeklyGoal.target} workouts
          </span>
          <strong>{goalPct}%</strong>
        </div>
        <div className="weekly-goal-track">
          <motion.div
            className="weekly-goal-fill"
            initial={{ width: 0 }}
            animate={{ width: `${Math.min(goalPct, 100)}%` }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
          />
        </div>
      </div>

      <WeeklyActivityChart data={data.dailyActivity} />
    </motion.section>
  );
};

export default WeeklySnapshot;
